import { useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import type { AppContext } from '../components/layout/AppLayout'
import { api, type DepositInfo } from '../lib/api'
import { haptics } from '../lib/haptics'

const NANOTON = 1_000_000_000n
const MIN_TON = 0.2

type Asset = 'TON' | 'USDT'

function tonToNano(ton: number): bigint {
  return BigInt(Math.round(ton * 1_000_000_000))
}

const PRESETS: Record<Asset, string[]> = {
  TON: ['1', '5', '20', '100'],
  USDT: ['5', '10', '50', '100'],
}

export default function DepositPage() {
  const { refreshBalance } = useOutletContext<AppContext>()
  const navigate = useNavigate()
  const [tonConnectUI] = useTonConnectUI()
  const wallet = useTonWallet()

  const [asset, setAsset] = useState<Asset>('TON')
  const [amount, setAmount] = useState('5')
  const [depositInfo, setDepositInfo] = useState<DepositInfo | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [txStatus, setTxStatus] = useState<'idle' | 'pending' | 'sent' | 'error'>('idle')
  const [txError, setTxError] = useState<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Clear polling interval on unmount (e.g. user navigates away after tx)
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    }
  }, [])

  // Telegram BackButton
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = window.Telegram?.WebApp as any
    tg?.BackButton?.show()
    const handler = () => navigate(-1)
    tg?.BackButton?.onClick(handler)
    return () => {
      tg?.BackButton?.hide()
      tg?.BackButton?.offClick(handler)
    }
  }, [navigate])

  // Fetch deposit info for rates
  useEffect(() => {
    const address = wallet?.account.address ?? ''
    api.getDepositInfo(address)
      .then(setDepositInfo)
      .catch(() => { /* non-critical */ })
  }, [wallet?.account.address])

  const amountNum = parseFloat(amount)
  const isInvalid = isNaN(amountNum) || amountNum <= 0
  const isBelowMin = !isNaN(amountNum) && amountNum > 0 && amountNum < MIN_TON

  const estimatedCredits = (() => {
    if (asset !== 'TON' || !depositInfo?.creditsPerTon || isInvalid || isBelowMin) return null
    const perTon = BigInt(depositInfo.creditsPerTon)
    return (perTon * tonToNano(amountNum)) / NANOTON
  })()

  const usdEstimate = (() => {
    if (asset !== 'TON' || !depositInfo?.tonPriceUsd || isInvalid) return null
    return (amountNum * depositInfo.tonPriceUsd).toFixed(2)
  })()

  const handleDeposit = async () => {
    if (asset !== 'TON') return
    if (isSending || isInvalid || isBelowMin) return
    const ton = parseFloat(amount)

    haptics.impact('medium')
    setIsSending(true)
    setTxStatus('pending')
    setTxError(null)

    try {
      const tonAddress = wallet?.account.address ?? ''
      const info = depositInfo ?? await api.getDepositInfo(tonAddress)

      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [{ address: info.depositAddress, amount: tonToNano(ton).toString() }],
      })

      haptics.notification('success')
      setTxStatus('sent')
      refreshBalance()
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = setInterval(refreshBalance, 5000)
      setTimeout(() => {
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null }
        setTxStatus('idle')
      }, 30000)
    } catch (err: unknown) {
      haptics.notification('error')
      const raw = err instanceof Error ? (err.message || '') : String(err ?? '')
      const msg = raw.length > 0 ? raw : 'Transaction failed'
      setTxError(msg.toLowerCase().includes('cancel') || msg.toLowerCase().includes('reject') ? 'Transaction cancelled' : msg)
      setTxStatus('error')
      setTimeout(() => setTxStatus('idle'), 3000)
    } finally {
      setIsSending(false)
    }
  }

  // Dynamic input width: approx 1ch per char
  const inputWidth = `${Math.max((amount || '0').length, 1) + 0.5}ch`

  return (
    <div
      className="flex flex-col flex-1 min-h-0"
      style={{ backgroundColor: 'var(--tg-theme-bg-color)' }}
    >
      {/* Page title */}
      <div
        className="flex items-center justify-center shrink-0"
        style={{ height: '48px', borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' }}
      >
        <span
          className="text-[17px] font-semibold"
          style={{ color: 'var(--tg-theme-text-color)' }}
        >
          Deposit
        </span>
      </div>

      {/* Asset selector tabs */}
      <div className="flex justify-center gap-sp-2 px-sp-4 py-sp-4 shrink-0">
        {(['TON', 'USDT'] as Asset[]).map((a) => (
          <button
            key={a}
            onClick={() => {
              haptics.selection()
              setAsset(a)
              setAmount(PRESETS[a][1])
              setTxError(null)
              setTxStatus('idle')
            }}
            className="relative h-9 px-sp-5 rounded-full text-[15px] font-semibold transition-all active:opacity-70"
            style={
              asset === a
                ? {
                    backgroundColor: 'var(--tg-theme-button-color)',
                    color: 'var(--tg-theme-button-text-color)',
                  }
                : {
                    backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                    color: 'var(--tg-theme-hint-color)',
                  }
            }
          >
            {a}
            {a === 'USDT' && asset !== 'USDT' && (
              <span
                className="absolute -top-1.5 -right-1 text-[9px] font-bold px-1 py-px rounded-full leading-none"
                style={{
                  backgroundColor: 'var(--tg-theme-hint-color)',
                  color: 'var(--tg-theme-bg-color)',
                  opacity: 0.7,
                }}
              >
                SOON
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Main amount area — grows to fill space */}
      <div className="flex-1 flex flex-col items-center justify-center px-sp-4">
        {asset === 'TON' ? (
          <>
            {/* Big amount input */}
            <div className="flex items-baseline gap-3">
              <input
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-transparent outline-none text-center tabular-nums"
                style={{
                  fontSize: 'clamp(44px, 16vw, 68px)',
                  fontWeight: 700,
                  lineHeight: 1,
                  width: inputWidth,
                  minWidth: '1.5ch',
                  maxWidth: '55vw',
                  color: isBelowMin
                    ? 'var(--tg-theme-destructive-text-color)'
                    : 'var(--tg-theme-text-color)',
                  caretColor: 'var(--tg-theme-accent-text-color)',
                  /* Remove number input arrows */
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                }}
              />
              <span
                className="font-bold"
                style={{
                  fontSize: 'clamp(22px, 8vw, 34px)',
                  color: 'var(--tg-theme-hint-color)',
                  lineHeight: 1,
                }}
              >
                TON
              </span>
            </div>

            {/* Estimates */}
            <div className="flex flex-col items-center mt-sp-3 gap-0.5">
              {usdEstimate && !isInvalid && (
                <p
                  className="text-[15px] tabular-nums"
                  style={{ color: 'var(--tg-theme-hint-color)' }}
                >
                  ≈ ${usdEstimate} USD
                </p>
              )}
              {estimatedCredits !== null && (
                <p
                  className="text-[13px] tabular-nums"
                  style={{ color: 'var(--tg-theme-hint-color)' }}
                >
                  ≈ {estimatedCredits.toLocaleString()} credits
                </p>
              )}
              {isBelowMin && (
                <p
                  className="text-[13px] font-semibold mt-1"
                  style={{ color: 'var(--tg-theme-destructive-text-color)' }}
                >
                  Minimum {MIN_TON} TON
                </p>
              )}
            </div>
          </>
        ) : (
          /* USDT — coming soon */
          <div className="flex flex-col items-center gap-sp-4 text-center px-sp-6">
            <span className="text-[56px]">💵</span>
            <p
              className="text-[20px] font-bold"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              USDT coming soon
            </p>
            <p
              className="text-[15px] leading-relaxed"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              USDT deposits will be available in a future update. Use TON to top up your balance right now.
            </p>
            <button
              onClick={() => { haptics.impact('light'); setAsset('TON'); setAmount('5') }}
              className="mt-sp-2 h-12 px-sp-8 rounded-[14px] text-[16px] font-semibold active:opacity-70 transition-opacity"
              style={{
                backgroundColor: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
              }}
            >
              Deposit TON instead
            </button>
          </div>
        )}
      </div>

      {/* Bottom controls — only for TON */}
      {asset === 'TON' && (
        <div className="shrink-0 px-sp-4 pb-sp-6 flex flex-col gap-sp-3">
          {/* Quick presets */}
          <div className="flex gap-sp-2">
            {PRESETS.TON.map((v) => (
              <button
                key={v}
                onClick={() => { haptics.impact('light'); setAmount(v) }}
                className="flex-1 h-11 rounded-[12px] text-[15px] font-medium transition-opacity active:opacity-70"
                style={
                  amount === v
                    ? {
                        backgroundColor: 'var(--tg-theme-button-color)',
                        color: 'var(--tg-theme-button-text-color)',
                      }
                    : {
                        backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                        color: 'var(--tg-theme-text-color)',
                      }
                }
              >
                {v}
              </button>
            ))}
          </div>

          {/* Status messages */}
          {txStatus === 'sent' && (
            <div
              className="p-sp-3 text-center rounded-[14px]"
              style={{
                backgroundColor: 'rgba(52, 199, 89, 0.12)',
                border: '1px solid rgba(52, 199, 89, 0.3)',
              }}
            >
              <p className="text-[15px] font-semibold" style={{ color: '#34c759' }}>
                ✓ Transaction sent
              </p>
              <p
                className="text-[13px] mt-0.5"
                style={{ color: 'var(--tg-theme-hint-color)' }}
              >
                Credits appear within ~30 sec after confirmation
              </p>
            </div>
          )}
          {txError && (
            <p
              className="text-[13px] text-center"
              style={{ color: 'var(--tg-theme-destructive-text-color)' }}
            >
              {txError}
            </p>
          )}

          {/* CTA button */}
          <button
            onClick={handleDeposit}
            disabled={isSending || txStatus === 'sent' || isBelowMin || isInvalid}
            className="w-full h-14 rounded-[16px] text-[17px] font-semibold transition-opacity disabled:opacity-40 active:opacity-70 flex items-center justify-center gap-sp-2"
            style={{
              backgroundColor: 'var(--tg-theme-button-color)',
              color: 'var(--tg-theme-button-text-color)',
            }}
          >
            {txStatus === 'pending' && (
              <>
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Confirming in wallet…
              </>
            )}
            {txStatus === 'sent' && '✓ Crediting…'}
            {txStatus === 'error' && 'Try again'}
            {txStatus === 'idle' && (
              isInvalid
                ? 'Enter amount'
                : isBelowMin
                  ? `Minimum ${MIN_TON} TON`
                  : `Deposit ${amount} TON →`
            )}
          </button>

          <p
            className="text-[12px] text-center"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            Min. {MIN_TON} TON · No comment required
          </p>
        </div>
      )}
    </div>
  )
}
